/**
 * SOC 2 readiness — V96-4-9. V98-3-2: rehosted inside the SOC 2 program
 * (ProgramShell, /w/:slug/app/programs/soc2/:tab) with `embedded` + `view`;
 * the old /w/:slug/app/soc2 route redirects there (query preserved).
 *   view 'prepare' — readiness, audit period, policies, criteria
 *   view 'audit'   — ended / fieldwork windows, auditor requests, period
 *   view 'operate' — readiness, policies, criteria
 *
 * Job: "Am I ready for my audit window, and what do I fix next?"
 * Reads (any member): frameworks/soc2/readiness, audit-periods,
 * certification-profiles, policies/status, policies, policies/mine,
 * policies/templates, audit-periods/:id/pbc, settings/team/members.
 * Writes:
 *   owner/admin — certification-profiles (SOC 2 profile on first period),
 *     audit-periods (create; PATCH while planned; freeze the ended window
 *     after the next one is saved; close only on explicit confirm),
 *     policies (publish), attestations/control (attest a control).
 *   any member — policies/:versionId/acknowledge (their own acknowledgment).
 *
 * Deep links: ?filter=gap|partial|unmapped|met|all · ?setup=period · ?acknowledge=1.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Checkbox, Drawer, Field, Input, PageHeader, Select, Spinner, StatusGlyph, Textarea } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import {
  ApiError,
  acknowledgeSoc2Policy,
  attestSoc2Control,
  closeAuditPeriod,
  createAuditPeriod,
  createSoc2Profile,
  freezeAuditPeriod,
  getPbcList,
  getPolicyAckStatus,
  getSoc2Readiness,
  listAllControls,
  listAuditPeriods,
  listCertificationProfiles,
  listPublishedSoc2Policies,
  listSoc2PolicyTemplates,
  listWorkspaceMembers,
  publishSoc2Policy,
  updateAuditPeriod,
  type AuditPeriodRow,
  type CertificationProfileRow,
  type PostureFieldError,
  type PublishedPolicy,
  type Soc2Readiness as Soc2ReadinessData,
  type Soc2Requirement,
  type Soc2RequirementStatus,
} from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { readinessPace } from '../lib/program-shell';
import {
  STATUS_META, SERIES_NAMES, addDays, addMonthsMinusDay, bucketOf, evidenceSummary, fmtDay, nextAction, pickSoc2Context, readinessSplit,
  seriesOf, seriesSort, soc2Verdict, windowStatus, worstFirst, type GlyphKind, type ReadinessSplit, type Verdict, type WindowStatus,
} from '../lib/soc2';
import { sha256Hex } from '../lib/sha256';
import { sourceNameOf } from '@aigovops/core';
import { coveringAcceptance, requirementItems } from '../lib/couldnt-check';
import { useOptionalAuth } from '../lib/auth';
import { AcceptedLine, AcceptRisk, CouldntCheckChip, CouldntCheckCount, CouldntCheckFixes, CouldntCheckWhy, ShowAllReasons } from '../components/CouldntCheck';
import { AskAdmin } from '../components/AskAdmin';
import { SafeMarkdown } from '../components/SafeMarkdown';
import { pendingPolicyKeys, usePendingPolicies } from '../components/PendingPolicies';
import './stages/lifecycle.css';
import './soc2-readiness.css';

// Re-exported for callers that imported them from the page in round 0.
export { evidenceSummary, nextAction, pickSoc2Context, windowStatus, fmtDay } from '../lib/soc2';

/** V109-1-13: `couldnt` — criteria GAC Pro couldn't check (LM-9). */
type Filter = 'needs' | Soc2RequirementStatus | 'couldnt' | 'all';
const FILTERS: Filter[] = ['needs', 'gap', 'partial', 'unmapped', 'met', 'couldnt', 'all'];

/** The visible page title, made focusable — a visible focus-return target. */
function pageHeading(): HTMLElement | null {
  // Embedded in ProgramShell the page title is the program H1 outside this block.
  const h = document.querySelector<HTMLElement>('.v96-soc2 [data-page-header] h1, .v96-soc2 h1') ?? document.querySelector<HTMLElement>('main h1');
  if (h && h.tabIndex !== -1) h.tabIndex = -1;
  return h;
}
/** R3-U24: back to the button that opened a form (it re-renders once the form closes), else the page title. */
const focusOpener = (id: string | null) => {
  window.setTimeout(() => { const el = id ? document.getElementById(id) : null; if (el) el.focus(); else pageHeading()?.focus(); }, 0);
};

// ---- small shared pieces ------------------------------------------------------------

export function VerdictPill({ verdict }: { verdict: Verdict }) {
  return <Badge className={`v75-status ${verdict.tone} v96-verdict`}><StatusGlyph kind={verdict.glyph} /> {verdict.label}</Badge>;
}

export function CountdownBadge({ window, verdict }: { window: WindowStatus; verdict: Verdict }) {
  const soon = window.phase === 'active' && window.days <= 30;
  const tone = verdict.tone === 'success' || verdict.key === 'fieldwork' ? 'neutral' : verdict.tone;
  return <Badge className={`v75-status ${tone}`}><StatusGlyph kind={soon || (window.phase === 'ended' && verdict.key !== 'fieldwork') ? 'warning' : 'clock'} /> {window.label}</Badge>;
}

function DrawerHead({ title, onClose, headingRef, closeLabel = 'Close' }: { title: string; onClose: () => void; headingRef?: React.RefObject<HTMLHeadingElement>; closeLabel?: string }) {
  return <div className="v96-drawer-head"><h2 ref={headingRef} tabIndex={-1}>{title}</h2><Button variant="ghost" onClick={onClose} aria-label={closeLabel}>✕</Button></div>;
}

function useAnnounce() {
  const [msg, setMsg] = useState('');
  const announce = useCallback((m: string) => { setMsg(''); window.setTimeout(() => setMsg(m), 50); }, []);
  return [msg, announce] as const;
}

// ---- page ------------------------------------------------------------------------

export type Soc2View = 'all' | 'prepare' | 'audit' | 'operate';

export function Soc2Readiness({ embedded = false, view = 'all', criteriaHref }: {
  embedded?: boolean; view?: Soc2View;
  /** Where the criteria list lives when this view hides it (ProgramShell: the Prepare tab). */
  criteriaHref?: string;
} = {}) {
  const navigate = useNavigate();
  // V98-3 review U5: each tab's body is keyed to ITS period — Audit shows the window in fieldwork and the
  // auditor's request bar (never the current window's readiness); Prepare shows readiness for the open window.
  const show = {
    unfrozen: view !== 'operate',
    fieldwork: view === 'all' || view === 'audit',
    criteria: view !== 'audit',
    readiness: view !== 'audit',
    requests: view === 'audit',
  };
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const editable = role === 'owner' || role === 'admin';
  const [params] = useSearchParams();
  const initialFilter = params.get('filter') as Filter | null;
  const [optional, setOptional] = useState(false);
  const [filter, setFilter] = useState<Filter>(initialFilter && FILTERS.includes(initialFilter) ? initialFilter : 'needs');
  const [periodForm, setPeriodForm] = useState<null | { mode: 'new' | 'next' | 'change' }>(null);
  const [drawer, setDrawer] = useState<null | { kind: 'publish' } | { kind: 'ack' } | { kind: 'pbc'; periodId: string } | { kind: 'close'; period: AuditPeriodRow } | { kind: 'attest'; controlId: string; requirement: Soc2Requirement }>(null);
  const [notice, announce] = useAnnounce();
  const queryClient = useQueryClient();

  const readiness = useQuery({ queryKey: queryKeys.soc2Readiness(slug, optional), queryFn: () => getSoc2Readiness(slug, optional), enabled: !!slug, placeholderData: keepPreviousData });
  const profiles = useQuery({ queryKey: queryKeys.certificationProfiles(slug), queryFn: () => listCertificationProfiles(slug), enabled: !!slug });
  const periods = useQuery({ queryKey: queryKeys.auditPeriods(slug), queryFn: () => listAuditPeriods(slug), enabled: !!slug });
  const policies = useQuery({ queryKey: queryKeys.policyAckStatus(slug), queryFn: () => getPolicyAckStatus(slug), enabled: !!slug });
  const { pending: pendingPolicies } = usePendingPolicies(slug);

  const ctx = useMemo(() => pickSoc2Context(profiles.data?.profiles ?? [], periods.data?.periods ?? []), [profiles.data, periods.data]);
  const pbc = useQuery({ queryKey: ['soc2-pbc', slug, ctx.period?.period_id], queryFn: () => getPbcList(slug, ctx.period!.period_id), enabled: !!slug && !!ctx.period });
  const fieldworkPbc = useQuery({ queryKey: ['soc2-pbc', slug, ctx.fieldwork?.period_id], queryFn: () => getPbcList(slug, ctx.fieldwork!.period_id), enabled: !!slug && !!ctx.fieldwork && ctx.fieldwork.period_id !== ctx.period?.period_id });
  const loadingContext = profiles.isLoading || periods.isLoading;
  const win = ctx.period ? windowStatus(ctx.period) : null;
  const ended = win?.phase === 'ended';

  // ?setup=period opens the form on arrival (from the Dashboard tile).
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || loadingContext || !editable) return;
    if (params.get('setup') === 'period' && (!ctx.period || ended)) { autoOpened.current = true; setPeriodForm({ mode: ended ? 'next' : 'new' }); }
  }, [loadingContext, editable, ctx.period, ended, params]);
  // ?acknowledge=1 (from the Dashboard / My Work reminder) opens the acknowledge drawer.
  const ackOpened = useRef(false);
  useEffect(() => {
    if (ackOpened.current || params.get('acknowledge') !== '1' || pendingPolicies.length === 0) return;
    ackOpened.current = true; setDrawer({ kind: 'ack' });
  }, [params, pendingPolicies.length]);

  const jumpTo = (status: 'gap' | 'partial' | 'couldnt') => {
    if (!show.criteria && criteriaHref) { navigate(`${criteriaHref}?filter=${status}`); return; }
    // V109-1-19 (H6): "N couldn't be checked" filters to them, then lands on the first fix link.
    if (status === 'couldnt') setFilter('couldnt');
    else if (filter !== 'needs' && filter !== status && filter !== 'all') setFilter('needs');
    window.setTimeout(() => {
      const el = status === 'couldnt'
        ? document.querySelector<HTMLElement>('[data-first-couldnt] [data-part="couldnt-check-fix"]')
        : document.querySelector<HTMLElement>(`[data-first-${status}] a, [data-first-${status}] button`);
      el?.scrollIntoView?.({ block: 'center' });
      el?.focus();
    }, 0);
  };
  const viewerId = useOptionalAuth()?.user?.id ?? '';
  const closeDrawer = useCallback(() => setDrawer(null), []);

  const moveToFieldwork = async (periodId: string) => {
    try { await freezeAuditPeriod(slug, periodId); announce('The ended window is now in fieldwork.'); await queryClient.invalidateQueries({ queryKey: queryKeys.auditPeriods(slug) }); }
    catch (e) { announce(e instanceof Error ? e.message : 'The window could not be moved to fieldwork. Try again.'); }
  };

  return <div className="v75-lifecycle v96-soc2">
    {embedded
      ? <p className="v75-muted"><PeriodLine loading={loadingContext} ctx={ctx} view={view} /></p>
      : <PageHeader title="SOC 2 readiness" subtitle={<PeriodLine loading={loadingContext} ctx={ctx} view={view} />} />}
    <div role="status" aria-live="polite" className={notice ? 'v96-soc2-notice' : 'sr-only'}>{notice}</div>

    {(profiles.error || periods.error) && <Alert variant="error"><p>The audit period could not load.</p><Button onClick={() => { void profiles.refetch(); void periods.refetch(); }}>Retry</Button></Alert>}
    {show.unfrozen && ctx.unfrozen.map(p => <section key={p.period_id} className="v96-fieldwork pending" aria-label="Ended window not yet in fieldwork">
      <span className="v96-fieldwork-label"><StatusGlyph kind="warning" /> Ended · not in fieldwork</span>
      <span>{fmtDay(p.period_start)} – {fmtDay(p.period_end)}</span>
      {editable ? <Button variant="secondary" onClick={() => void moveToFieldwork(p.period_id)}>Move to fieldwork</Button>
        : <AskAdmin slug={slug} compact verb="move it to fieldwork" what="moves ended windows to fieldwork" subject="SOC 2: move the ended audit window to fieldwork" />}
    </section>)}

    {show.fieldwork && ctx.fieldwork && <FieldworkStrip period={ctx.fieldwork} pbc={show.requests ? null : (ctx.fieldwork.period_id === ctx.period?.period_id ? pbc.data : fieldworkPbc.data)?.counts ?? null}
      editable={editable} onPbc={() => setDrawer({ kind: 'pbc', periodId: ctx.fieldwork!.period_id })} onClose={() => setDrawer({ kind: 'close', period: ctx.fieldwork! })} />}
    {show.requests && ctx.fieldwork ? (
      <RequestBar counts={(ctx.fieldwork.period_id === ctx.period?.period_id ? pbc.data : fieldworkPbc.data)?.counts ?? null}
        loading={ctx.fieldwork.period_id === ctx.period?.period_id ? pbc.isLoading : fieldworkPbc.isLoading}
        onOpen={() => setDrawer({ kind: 'pbc', periodId: ctx.fieldwork!.period_id })} />
    ) : null}
    {show.requests && !loadingContext && !ctx.fieldwork && !ctx.unfrozen.length ? (
      <section className="v96-strip" aria-label="Fieldwork">
        <div className="v96-strip-empty">
          <span className="v96-meter-label">No window in fieldwork</span>
          <span className="v96-meter-text">{ctx.period ? `The ${fmtDay(ctx.period.period_start)} – ${fmtDay(ctx.period.period_end)} window goes to fieldwork when it ends.` : 'Set the audit window first.'}</span>
          {criteriaHref ? <Link to={criteriaHref}>Go to Prepare</Link> : null}
        </div>
      </section>
    ) : null}

    {periodForm && editable && <PeriodForm slug={slug} profile={ctx.profile}
      previous={periodForm.mode === 'next' ? ctx.period : null} editing={periodForm.mode === 'change' ? ctx.period : null}
      others={(periods.data?.periods ?? []).filter(p => p.status !== 'closed' && p.profile_id === ctx.profile?.profile_id)}
      onCancel={() => { const back = periodForm.mode === 'change' ? 'soc2-change-period' : null; setPeriodForm(null); focusOpener(back); }}
      onSaved={(result) => { const back = periodForm.mode === 'change' ? 'soc2-change-period' : null; setPeriodForm(null); announce(result.freezeFailed ? 'Audit period saved. The previous window is not in fieldwork yet — move it below.' : 'Audit period saved.'); focusOpener(back); }} />}

    {readiness.isLoading ? <Spinner label="Loading SOC 2 readiness" />
      : readiness.error && !readiness.data ? <Alert variant="error"><p>SOC 2 readiness could not load.</p><Button onClick={() => void readiness.refetch()}>Retry</Button></Alert>
      // V101-6-3: zero criteria loaded is not "every criterion met" (it read as success while checks failed).
      : readiness.data && show.readiness && readiness.data.counts.total === 0 ? <Card>
        <h3>No SOC 2 criteria are loaded yet</h3>
        {editable
          ? <p><Link to={`/w/${slug}/app/queue`}>Review the SOC 2 criteria in the content queue</Link></p>
          : <AskAdmin slug={slug} compact roles={['owner', 'admin']} what="loads the SOC 2 criteria" verb="load the SOC 2 criteria" subject="Load the SOC 2 criteria" />}
      </Card>
      : readiness.data && show.readiness && <>
        <Hero embedded={embedded} data={readiness.data} window={win} periodStatus={ctx.period?.status} byDate={ctx.period?.period_end ?? null} loadingContext={loadingContext} editable={editable} slug={slug}
          hasPeriod={!!ctx.period} formOpen={!!periodForm} canChange={editable && ctx.period?.status === 'planned' && win?.phase !== 'ended'}
          onChangePeriod={() => setPeriodForm({ mode: 'change' })}
          onSetPeriod={() => setPeriodForm({ mode: ended ? 'next' : 'new' })}
          onFixGaps={() => jumpTo('gap')} onReviewPartial={() => { setFilter('partial'); jumpTo('partial'); }}
          onCouldnt={() => jumpTo('couldnt')} />
        <SecondaryStrip policyPct={policies.data?.overall_pct ?? null} policiesLoaded={!!policies.data} policyError={!!policies.error}
          onRetryPolicies={() => void policies.refetch()} pbc={pbc.data?.counts ?? null} editable={editable} slug={slug}
          pending={pendingPolicies.length}
          onPublish={() => setDrawer({ kind: 'publish' })} onAck={() => setDrawer({ kind: 'ack' })}
          onPbc={ctx.period ? () => setDrawer({ kind: 'pbc', periodId: ctx.period!.period_id }) : undefined} />
        {show.criteria && <Criteria requirements={readiness.data.requirements} filter={filter} onFilter={setFilter} optional={optional} onOptional={setOptional}
          prefix={prefix} refreshing={readiness.isFetching} editable={editable} slug={slug} viewerId={viewerId}
          onAttest={(controlId, requirement) => setDrawer({ kind: 'attest', controlId, requirement })} />}
      </>}

    {drawer?.kind === 'publish' && editable && <PublishPolicyDrawer slug={slug} published={policies.data?.policies.map(p => p.policy_key) ?? []}
      onClose={closeDrawer} onPublished={(title, ack) => { setDrawer(null); announce(ack ? `${title} published. Members can acknowledge it from this page.` : `${title} published.`); }} />}
    {drawer?.kind === 'ack' && <AcknowledgeDrawer slug={slug} pending={pendingPolicies} onClose={closeDrawer}
      onDone={(n) => { setDrawer(null); announce(`${n} polic${n === 1 ? 'y' : 'ies'} acknowledged.`); }} />}
    {drawer?.kind === 'pbc' && <PbcDrawer slug={slug} periodId={drawer.periodId} onClose={closeDrawer} />}
    {drawer?.kind === 'close' && editable && <CloseWindowDrawer slug={slug} period={drawer.period} onClose={closeDrawer}
      onClosed={() => { setDrawer(null); announce('Audit window closed.'); }} />}
    {drawer?.kind === 'attest' && editable && <AttestDrawer slug={slug} controlId={drawer.controlId} requirement={drawer.requirement} onClose={closeDrawer}
      onDone={(m) => { setDrawer(null); announce(m); }} />}
  </div>;
}

/**
 * The period line, keyed to the open tab (U5): Audit names the window IN FIELDWORK, every other view the
 * current window — a Type II window is an "observation window", a Type I an "as of" date.
 */
function PeriodLine({ loading, ctx, view }: { loading: boolean; ctx: ReturnType<typeof pickSoc2Context>; view: Soc2View }) {
  if (loading) return <>Loading audit period…</>;
  const p = view === 'audit' ? ctx.fieldwork ?? ctx.period : ctx.period;
  if (!p) return <>{ctx.lastClosed ? `Last window closed ${fmtDay(ctx.lastClosed.period_end)}` : 'No audit period set'}</>;
  return <>{windowLabel(p)}{p.status === 'fieldwork' ? ' · in fieldwork' : ''}</>;
}

/** "Type II · observation window Jan 1 – Dec 31, 2026" / "Type I · as of Mar 31, 2026". */
export function windowLabel(p: Pick<AuditPeriodRow, 'report_type' | 'period_start' | 'period_end'>): string {
  return p.report_type === 'type1' ? `Type I · as of ${fmtDay(p.period_start)}` : `Type II · observation window ${fmtDay(p.period_start)} – ${fmtDay(p.period_end)}`;
}

/** The auditor's request bar for the window in fieldwork (Audit tab, U5). */
function RequestBar({ counts, loading, onOpen }: { counts: { on_file: number; partial: number; needed: number; total: number } | null; loading: boolean; onOpen: () => void }) {
  return <section className="v96-strip" aria-label="Auditor requests">
    <div className="v96-strip-item">
      {loading ? <p className="v96-meter-label">Auditor requests…</p>
        : counts && counts.total > 0
          ? <><Meter label="Auditor requests on file" value={(counts.on_file / counts.total) * 100} text={`${counts.on_file} of ${counts.total} · ${counts.partial} partial · ${counts.needed} needed`} />
              <div className="v96-strip-actions"><Button variant="secondary" onClick={onOpen}>{counts.needed ? `Provide ${counts.needed} requests` : 'View requests'}</Button></div></>
          : <div className="v96-strip-empty"><span className="v96-meter-label">Auditor requests</span><span className="v96-meter-text">None yet for this window</span></div>}
    </div>
  </section>;
}

function FieldworkStrip({ period, pbc, editable, onPbc, onClose }: { period: AuditPeriodRow; pbc: { on_file: number; total: number } | null; editable: boolean; onPbc: () => void; onClose: () => void }) {
  // The dates are the OBSERVATION window the auditor is testing — not the fieldwork dates (U5 label fix).
  return <section className="v96-fieldwork" aria-label="Window in fieldwork">
    <span className="v96-fieldwork-label"><StatusGlyph kind="clock" /> In fieldwork</span>
    <span>{period.report_type === 'type1' ? `as of ${fmtDay(period.period_start)}` : `observation window ${fmtDay(period.period_start)} – ${fmtDay(period.period_end)}`}</span>
    {pbc && pbc.total > 0 && <Button variant="ghost" onClick={onPbc}>{`Auditor requests ${pbc.on_file}/${pbc.total}`}</Button>}
    {editable && <Button variant="secondary" onClick={onClose}>Close after report issued</Button>}
  </section>;
}

function Hero({ embedded = false, data, window, byDate = null, periodStatus, loadingContext, editable, slug, hasPeriod, formOpen, canChange, onChangePeriod, onSetPeriod, onFixGaps, onReviewPartial, onCouldnt }: {
  /** In ProgramShell the VerdictBand is the page's one verdict — no second pill here. */
  embedded?: boolean; data: Soc2ReadinessData; window: WindowStatus | null;
  /** R3-U15: the date the pace runs to (the open window's end). */
  byDate?: string | null;
  periodStatus?: AuditPeriodRow['status']; loadingContext: boolean; editable: boolean; slug: string;
  hasPeriod: boolean; formOpen: boolean; canChange: boolean; onChangePeriod: () => void; onSetPeriod: () => void; onFixGaps: () => void; onReviewPartial: () => void;
  /** V109-1-13: show the criteria GAC Pro couldn't check. */
  onCouldnt?: () => void;
}) {
  // V109-1-19 (B5): the hero counts what the list shows — Couldn't check is its own number, never a gap or partial.
  const split = readinessSplit(data.requirements);
  const couldnt = split.couldnt;
  const verdict = soc2Verdict({ ...data.counts, gap: split.gap, partial: split.partial + split.couldnt }, window, periodStatus);
  const ended = window?.phase === 'ended';
  const pace = readinessPace(data.counts.total - data.counts.met, window && window.phase === 'active' ? window.days : null, ['criterion', 'criteria'], window?.phase === 'active' ? byDate : null);
  const { gap, partial } = split;
  let action: JSX.Element | null = null;
  if (!loadingContext && (!hasPeriod || ended)) {
    action = editable && !formOpen ? <Button variant="primary" onClick={onSetPeriod}>{ended ? 'Start next audit period' : 'Set audit period'}</Button> : null;
  } else if (gap > 0) {
    action = <Button variant={formOpen ? 'secondary' : 'primary'} onClick={onFixGaps}>{`Fix ${gap} gap${gap === 1 ? '' : 's'}`}</Button>;
  } else if (partial > 0) {
    action = <Button variant={formOpen ? 'secondary' : 'primary'} onClick={onReviewPartial}>{`Review ${partial} partial`}</Button>;
  }
  return <section className="v96-hero" aria-labelledby="soc2-hero-title">
    <h2 id="soc2-hero-title" className="sr-only">Readiness summary</h2>
    <div className="v96-hero-main">
      {!loadingContext && !embedded && <VerdictPill verdict={verdict} />}
      <p className="v96-hero-metric"><span className="v96-hero-value">{Math.round(data.readiness_pct)}%</span><span className="v96-hero-unit">ready · {data.counts.met} of {data.counts.total} criteria met</span></p>
      {window && <CountdownBadge window={window} verdict={verdict} />}
      {/* R3-U15: the delta (criteria left to meet) and the weekly pace that meets them by the window's end. */}
      {pace ? <p className="v96-hero-pace" data-part="pace"><b>{pace.delta}</b>{pace.pace ? <span className="v75-muted">{` · ${pace.pace}`}</span> : null}</p> : null}
      {/* V109-1-13 (LM-9): never counted as met or as a gap closed — shown on its own, with the way in
          (V109-1-19 H6: it filters, then lands on the first fix; one mention, no second chip). */}
      {onCouldnt ? <CouldntCheckCount count={couldnt} noun={['criterion', 'criteria']} onClick={onCouldnt} /> : null}
    </div>
    <div className="v96-hero-action">
      {canChange && !formOpen && <Button id="soc2-change-period" variant="ghost" onClick={onChangePeriod}>Change period</Button>}
      {action}
      {!editable && !loadingContext && (!hasPeriod || ended) && <AskAdmin slug={slug} what="sets the audit period" subject="SOC 2 audit period in GAC Pro" />}
    </div>
  </section>;
}

function Meter({ label, value, target, text }: { label: string; value: number; target?: number; text: string }) {
  const v = Math.max(0, Math.min(100, value));
  const below = target !== undefined && v < target;
  return <div className="v96-meter">
    <div className="v96-meter-head" aria-hidden="true"><span className="v96-meter-label">{label}</span><span className="v96-meter-text">{text}</span></div>
    <div className="v96-meter-track" role="img" aria-label={`${label}: ${text}${below ? `, below the ${target}% target` : ''}`}>
      <div className={`v96-meter-fill${below ? ' below' : ''}`} style={{ width: `${v}%` }} />
    </div>
  </div>;
}

function SecondaryStrip({ policyPct, policiesLoaded, policyError, onRetryPolicies, pbc, editable, slug, pending, onPublish, onAck, onPbc }: {
  policyPct: number | null; policiesLoaded: boolean; policyError: boolean; onRetryPolicies: () => void; pbc: { on_file: number; total: number } | null;
  editable: boolean; slug: string; pending: number; onPublish: () => void; onAck: () => void; onPbc?: () => void;
}) {
  return <section className="v96-strip" aria-label="Audit preparation">
    <div className="v96-strip-item">
      {policyError ? <p className="v96-meter-label">Policy acknowledgment unavailable <Button variant="ghost" onClick={onRetryPolicies}>Retry</Button></p>
        : !policiesLoaded ? <p className="v96-meter-label">Policy acknowledgment…</p>
        : policyPct === null ? <div className="v96-strip-empty">
            <span className="v96-meter-label">Policy acknowledgment</span>
            <span className="v96-meter-text">No policies published</span>
            {editable ? <Button variant="secondary" onClick={onPublish}>Publish a policy</Button> : <AskAdmin slug={slug} what="publishes policies" subject="SOC 2 policies in GAC Pro" />}
          </div>
        : <Meter label="Policy acknowledgment" value={policyPct} target={95} text={`${Math.round(policyPct)}%`} />}
      {(pending > 0 || (editable && policyPct !== null)) && <div className="v96-strip-actions">
        {pending > 0 && <Button variant="secondary" onClick={onAck}>{`Acknowledge policies (${pending})`}</Button>}
        {editable && policyPct !== null && <Button variant="ghost" onClick={onPublish}>Publish a policy</Button>}
      </div>}
    </div>
    <div className="v96-strip-item">
      {pbc && pbc.total > 0
        ? <><Meter label="Auditor requests on file" value={(pbc.on_file / pbc.total) * 100} text={`${pbc.on_file} of ${pbc.total}`} />
            {onPbc && <div className="v96-strip-actions"><Button variant="ghost" onClick={onPbc}>View requests</Button></div>}</>
        : <div className="v96-strip-empty"><span className="v96-meter-label">Auditor requests</span><span className="v96-meter-text">Available once an audit window is set</span></div>}
    </div>
  </section>;
}

/** V109-1-19 (B5): Couldn't check is its own segment, with its own glyph, between gaps and partial. */
const SEG_META: Record<keyof Omit<ReadinessSplit, 'total'>, { label: string; glyph: GlyphKind }> = {
  gap: { label: STATUS_META.gap.label, glyph: STATUS_META.gap.glyph },
  couldnt: { label: "Couldn't check", glyph: 'unknown' },
  partial: { label: STATUS_META.partial.label, glyph: STATUS_META.partial.glyph },
  unmapped: { label: STATUS_META.unmapped.label, glyph: STATUS_META.unmapped.glyph },
  met: { label: STATUS_META.met.label, glyph: STATUS_META.met.glyph },
};

/**
 * One criterion row. V109-1-13 / V109-1-19 (LM-9): a criterion GAC Pro couldn't check says why in
 * Evidence (each reason once) and its fix in Next step — one mention each, the fix named for its
 * source. R3: one "Show all N reasons" toggle for both columns; an open acceptance is shown (R2-H5).
 */
function CritRow({ r, items, grouped, prefix, actionFor, ownerName, firstGap, firstPartial, firstCouldnt }: {
  r: Soc2Requirement; items: ReturnType<typeof requirementItems>; grouped: boolean; prefix: string; actionFor: (r: Soc2Requirement) => JSX.Element;
  ownerName: (userId: string) => string | null; firstGap: boolean; firstPartial: boolean; firstCouldnt: boolean;
}) {
  const [all, setAll] = useState(false);
  const meta = STATUS_META[r.status];
  const series = seriesOf(r);
  const couldnt = items.length > 0;
  const accepted = couldnt ? coveringAcceptance(r.risk_acceptances) : null;
  return <li className="v96-crit-row" data-check-state={couldnt ? 'couldnt_check' : r.check_state}
    data-first-gap={firstGap ? '' : undefined} data-first-partial={firstPartial ? '' : undefined} data-first-couldnt={firstCouldnt ? '' : undefined}>
    <div className="v96-crit-name"><span className="v96-ref">{r.ref}</span> <span>{r.title}</span>{!grouped && SERIES_NAMES[series] && <small>{SERIES_NAMES[series]}</small>}</div>
    <div className="v96-crit-status"><span className="sr-only">Status: </span>{couldnt ? <CouldntCheckChip /> : <Badge className={`v75-status ${meta.tone}`}><StatusGlyph kind={meta.glyph} /> {meta.label}</Badge>}</div>
    <div className="v96-crit-evidence"><span className="sr-only">Evidence: </span>{couldnt ? <>
      <CouldntCheckWhy items={items} unit="control" expanded={all} />
      {accepted ? <AcceptedLine acceptance={accepted} ownerName={ownerName(accepted.owner_user_id)} /> : null}
      <ShowAllReasons items={items} expanded={all} onToggle={() => setAll((x) => !x)} />
    </> : evidenceSummary(r)}</div>
    <div className="v96-crit-action"><span className="sr-only">Next step: </span>{couldnt
      // SOC 2 has no statement editor or objective upload: a value fix opens its Setup step; evidence
      // opens the library upload for the test.
      ? <CouldntCheckFixes items={items} prefix={prefix} ctx={{ ref: r.ref, returnTo: `${prefix}/programs/soc2/prepare?filter=couldnt` }} compact expanded={all} />
      : actionFor(r)}</div>
  </li>;
}

function Criteria({ requirements, filter, onFilter, optional, onOptional, prefix, refreshing, editable, slug, viewerId, onAttest }: {
  requirements: Soc2Requirement[]; filter: Filter; onFilter: (f: Filter) => void; optional: boolean; onOptional: (v: boolean) => void;
  prefix: string; refreshing: boolean; editable: boolean; slug: string; viewerId: string; onAttest: (controlId: string, r: Soc2Requirement) => void;
}) {
  const queryClient = useQueryClient();
  const members = useQuery({ queryKey: ['ws-members', slug], queryFn: () => listWorkspaceMembers(slug), staleTime: 60_000, retry: false });
  const ownerName = (userId: string) => members.data?.members.find((m) => m.user_id === userId)?.display_name ?? null;
  const onAccepted = () => queryClient.invalidateQueries({ queryKey: ['soc2-readiness', slug] });
  // V109-1-19 (B5): every count and filter uses the same buckets; a criterion GAC Pro couldn't check is
  // never inside Gap / Partial / Not mapped. "Needs work" stays everything not met.
  const counts = useMemo(() => readinessSplit(requirements), [requirements]);
  const visible = useMemo(() => requirements.filter(r => filter === 'all' ? true : filter === 'needs' ? r.status !== 'met'
    : bucketOf(r) === filter).sort(worstFirst), [requirements, filter]);
  const grouped = filter === 'all' || filter === 'met';
  const groups = useMemo(() => {
    if (!grouped) return [];
    const m = new Map<string, Soc2Requirement[]>();
    for (const r of visible) { const k = seriesOf(r); m.set(k, [...(m.get(k) ?? []), r]); }
    return [...m.entries()].sort(([a], [b]) => seriesSort(a, b));
  }, [visible, grouped]);
  const needs = counts.total - counts.met;
  const firstGapId = visible.find(r => bucketOf(r) === 'gap')?.requirement_id;
  const firstPartialId = visible.find(r => bucketOf(r) === 'partial')?.requirement_id;
  const firstCouldntId = visible.find(r => bucketOf(r) === 'couldnt')?.requirement_id;
  const pageUrl = typeof location !== 'undefined' ? location.href : '';
  const segs: Array<keyof typeof SEG_META> = ['gap', 'couldnt', 'partial', 'unmapped', 'met'];

  const actionFor = (r: Soc2Requirement) => {
    const a = nextAction(r, prefix, pageUrl);
    if (!a) return <span className="sr-only">No action needed</span>;
    if (a.kind === 'link') return <Link to={a.href} aria-label={`${a.label} for ${r.ref}`}>{a.label}</Link>;
    if (a.kind === 'mailto') return <a href={a.href} aria-label={`${a.label} for ${r.ref}`}>{a.label}</a>;
    if (editable) return <button type="button" className="v96-linkbtn" onClick={() => onAttest(a.controlId, r)} aria-label={`Attest ${r.ref}`}>Attest</button>;
    return <AskAdmin slug={slug} compact verb="attest" what="attests controls" subject={`SOC 2: please attest ${r.ref} ${r.title}`} />;
  };

  const row = (r: Soc2Requirement) => {
    const items = r.status === 'met' ? [] : requirementItems(r);
    return <CritRow key={r.requirement_id} r={r} items={items} grouped={grouped} prefix={prefix} actionFor={actionFor} ownerName={ownerName}
      firstGap={r.requirement_id === firstGapId} firstPartial={r.requirement_id === firstPartialId} firstCouldnt={r.requirement_id === firstCouldntId} />;
  };
  const list = (rows: Soc2Requirement[]) => <ul className="v96-crit-list">{rows.map(row)}</ul>;
  // V109-1-19 R3 (R2-H5 / Medium): ONE "Accept the risk while this is fixed" for the list (owner / admin), for
  // the criteria GAC Pro couldn't check that no open acceptance covers yet.
  const acceptTargets = requirements.filter((r) => r.status !== 'met' && bucketOf(r) === 'couldnt' && !coveringAcceptance(r.risk_acceptances)).map((r) => {
    const items = requirementItems(r);
    const conns = [...new Set(items.map((i) => i.kind).filter((k): k is string => !!k))];
    return {
      label: `${r.ref} ${r.title}`, registerItemId: r.requirement_id,
      controlId: r.crediting_controls.find((c) => c.state !== 'pass' && c.state !== 'not_applicable')?.control_id ?? null,
      connection: conns.length === 1 ? sourceNameOf(conns[0]!) : null,
    };
  });

  return <Card><section className="v75-panel" aria-labelledby="criteria-title">
    <div className="v75-page-heading">
      <h2 id="criteria-title">Trust services criteria</h2>
      <label className="v96-toggle"><input type="checkbox" checked={optional} onChange={e => onOptional(e.currentTarget.checked)} /> Include optional categories</label>
    </div>
    <div className="v96-filterbar" role="group" aria-label="Filter criteria by status">
      <button type="button" className="v96-textchip" aria-pressed={filter === 'needs'} onClick={() => onFilter('needs')}>Needs work ({needs})</button>
      <button type="button" className="v96-textchip" aria-pressed={filter === 'all'} onClick={() => onFilter('all')}>All ({counts.total})</button>
      <div className="v96-segbar">
        {segs.map(s => counts[s] > 0 && <button key={s} type="button" className={`v96-seg${s === 'gap' ? ' danger' : ''}`} data-seg={s} aria-pressed={filter === s} onClick={() => onFilter(filter === s ? 'needs' : s)}>
          <StatusGlyph kind={SEG_META[s].glyph} /> {counts[s]} {SEG_META[s].label}
        </button>)}
      </div>
    </div>
    {editable && counts.couldnt > 0
      ? <div className="v96-crit-tools" data-part="couldnt-list-tools"><AcceptRisk slug={slug} viewerId={viewerId} targets={acceptTargets} onDone={() => void onAccepted()} /></div>
      : null}
    <p className="sr-only" role="status" aria-live="polite">{`Showing ${visible.length} criteria`}</p>
    {visible.length === 0
      ? <div className="v75-empty"><h3>{filter === 'needs' ? 'Every criterion is met' : 'No criteria in this view'}</h3><Button variant="secondary" onClick={() => onFilter('all')}>Show all criteria</Button></div>
      : <div className={`v96-crit${refreshing ? ' refreshing' : ''}`} aria-busy={refreshing || undefined}>
        <div className="v96-crit-head" aria-hidden="true"><span>Criterion</span><span>Status</span><span>Evidence</span><span>Next step</span></div>
        {grouped
          ? groups.map(([series, rows]) => <div key={series}>
              <h3 className="v96-group">{series}{SERIES_NAMES[series] ? ` · ${SERIES_NAMES[series]}` : ''}</h3>
              {list(rows)}
            </div>)
          : list(visible)}
      </div>}
  </section></Card>;
}

// ---- set / change / start next audit period -----------------------------------------

function PeriodForm({ slug, profile, previous, editing, others, onCancel, onSaved }: {
  slug: string; profile: CertificationProfileRow | null; previous: AuditPeriodRow | null; editing: AuditPeriodRow | null;
  /** Non-closed windows of this profile; a new or changed window may not overlap any of them. */
  others: AuditPeriodRow[];
  onCancel: () => void; onSaved: (r: { freezeFailed?: string }) => void;
}) {
  const queryClient = useQueryClient();
  const fixedType = editing?.report_type ?? profile?.report_type ?? null;
  const initialStart = editing ? editing.period_start.slice(0, 10) : previous ? addDays(previous.period_end, 1) : '';
  const [type, setType] = useState<'type1' | 'type2'>(fixedType ?? 'type2');
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(editing ? editing.period_end.slice(0, 10) : initialStart ? addMonthsMinusDay(initialStart, 12) : '');
  const [endTouched, setEndTouched] = useState(!!editing);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, []);

  const onStart = (v: string) => { setStart(v); if (type === 'type2' && !endTouched && v) setEnd(addMonthsMinusDay(v, 12)); };
  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.auditPeriods(slug) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.certificationProfiles(slug) }),
  ]);

  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (saving) return; setFormError(null);
    const errs: Record<string, string> = {};
    if (!start) errs.start = 'Choose the first day of the audit window.';
    if (type === 'type2') {
      if (!end) errs.end = 'Choose the last day of the audit window.';
      else if (start && (end < addMonthsMinusDay(start, 3) || end > addMonthsMinusDay(start, 12))) errs.end = 'A Type II window runs 3 to 12 months.';
    }
    if (previous && start && start <= previous.period_end) errs.start = `Start after the last window (${fmtDay(previous.period_end)}).`;
    const spanEnd = type === 'type2' ? end : start;
    const clash = start && spanEnd ? others.find(o => o.period_id !== editing?.period_id && start <= o.period_end.slice(0, 10) && spanEnd >= o.period_start.slice(0, 10)) : undefined;
    if (clash && !errs.start) errs.start = `Overlaps the window ${fmtDay(clash.period_start)} – ${fmtDay(clash.period_end)}.`;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    const span = { period_start: start, ...(type === 'type2' ? { period_end: end } : {}) };
    try {
      if (editing) {
        await updateAuditPeriod(slug, editing.period_id, span);
        await invalidate();
        onSaved({});
        return;
      }
      // Create the new window first: if that fails, nothing has changed.
      const profileId = profile?.profile_id ?? (await createSoc2Profile(slug, type)).profile.profile_id;
      await createAuditPeriod(slug, { profile_id: profileId, ...span });
      // Then move the ended window to fieldwork (seal its evidence). Never close it here.
      let freezeFailed: string | undefined;
      if (previous && previous.status === 'planned') {
        try { await freezeAuditPeriod(slug, previous.period_id); } catch { freezeFailed = previous.period_id; }
      }
      await invalidate();
      onSaved({ freezeFailed });
    } catch (err) {
      const body = err instanceof ApiError ? err.body as { errors?: PostureFieldError[] } | undefined : undefined;
      const fieldErrs: Record<string, string> = {};
      const rest: string[] = [];
      for (const fe of body?.errors ?? []) {
        if (fe.path === 'period_start') fieldErrs.start = fe.message;
        else if (fe.path === 'period_end') fieldErrs.end = fe.message;
        else rest.push(fe.message);
      }
      setErrors(fieldErrs);
      const conflict = err instanceof ApiError && err.status === 409 && editing ? 'This window has started fieldwork, so it can no longer be changed.' : null;
      if (conflict) setFormError(conflict);
      else if (rest.length || !Object.keys(fieldErrs).length) setFormError(rest.join(' ') || (err instanceof Error ? err.message : 'The audit period could not be saved. Please retry.'));
      void invalidate();
    } finally { setSaving(false); }
  };

  const title = editing ? 'Change audit period' : previous ? 'Start next audit period' : 'Set audit period';
  return <Card><form className="v75-panel v96-period-form" onSubmit={submit} noValidate aria-labelledby="period-form-title">
    <h2 id="period-form-title" ref={heading} tabIndex={-1}>{title}</h2>
    {previous && previous.status === 'planned' && <p className="v75-muted">After saving, {fmtDay(previous.period_start)} – {fmtDay(previous.period_end)} moves to fieldwork.</p>}
    {formError && <Alert variant="error">{formError}</Alert>}
    <div className="v96-period-fields">
      <Field id="soc2-report-type" label="Report type" hint={fixedType ? 'Set by your SOC 2 profile.' : undefined}>
        <Select value={type} disabled={!!fixedType} onChange={e => setType(e.currentTarget.value as 'type1' | 'type2')}>
          <option value="type2">Type II (window)</option>
          <option value="type1">Type I (point in time)</option>
        </Select>
      </Field>
      <Field id="soc2-period-start" label={type === 'type2' ? 'Window start' : 'Report date'} required error={errors.start}>
        <Input type="date" value={start} onChange={e => onStart(e.currentTarget.value)} />
      </Field>
      {type === 'type2' && <Field id="soc2-period-end" label="Window end" required error={errors.end} hint="3 to 12 months after the start.">
        <Input type="date" value={end} onChange={e => { setEnd(e.currentTarget.value); setEndTouched(true); }} />
      </Field>}
    </div>
    <div className="v75-actions">
      <Button type="submit" variant="primary" aria-disabled={saving || undefined}>{saving ? 'Saving…' : 'Save audit period'}</Button>
      <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
    </div>
  </form></Card>;
}

// ---- drawers ---------------------------------------------------------------------------

function CloseWindowDrawer({ slug, period, onClose, onClosed }: { slug: string; period: AuditPeriodRow; onClose: () => void; onClosed: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const confirm = async () => {
    if (saving) return;
    setSaving(true); setError(null);
    try { await closeAuditPeriod(slug, period.period_id); await queryClient.invalidateQueries({ queryKey: queryKeys.auditPeriods(slug) }); onClosed(); }
    catch (e) { setError(e instanceof Error ? e.message : 'The window could not be closed.'); }
    finally { setSaving(false); }
  };
  return <Drawer open modal onClose={onClose} label="Close audit window" className="v96-drawer" returnFocus={pageHeading}>
    <DrawerHead title="Close audit window" onClose={onClose} closeLabel="Dismiss dialog" />
    <div className="v96-form">
      <p><strong>{fmtDay(period.period_start)} – {fmtDay(period.period_end)}</strong></p>
      <p className="v75-muted">Close it only after the auditor has issued the report. This cannot be undone.</p>
      {error && <Alert variant="error">{error}</Alert>}
      <div className="v75-actions">
        <Button variant="danger" aria-disabled={saving || undefined} onClick={() => void confirm()}>{saving ? 'Closing…' : 'Close audit window'}</Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  </Drawer>;
}

function PbcDrawer({ slug, periodId, onClose }: { slug: string; periodId: string; onClose: () => void }) {
  const q = useQuery({ queryKey: ['soc2-pbc', slug, periodId], queryFn: () => getPbcList(slug, periodId), enabled: !!slug });
  const order = { needed: 0, partial: 1, on_file: 2 } as const;
  const label = { needed: 'Needed', partial: 'Partial', on_file: 'On file' } as const;
  const glyph = { needed: 'x', partial: 'half', on_file: 'check' } as const;
  const tone = { needed: 'danger', partial: 'warning', on_file: 'success' } as const;
  const items = [...(q.data?.items ?? [])].sort((a, b) => order[a.status] - order[b.status]);
  return <Drawer open modal onClose={onClose} label="Auditor requests" className="v96-drawer" returnFocus={pageHeading}>
    <DrawerHead title="Auditor requests" onClose={onClose} />
    {q.isLoading ? <Spinner label="Loading auditor requests" />
      : q.error ? <Alert variant="error"><p>Auditor requests could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>
      : <ul className="v96-pbc">
        {items.map(i => <li key={i.key}>
          <div><span className="v96-ref">{i.ref}</span> {i.request}</div>
          <Badge className={`v75-status ${tone[i.status]}`}><StatusGlyph kind={glyph[i.status]} /> {label[i.status]}</Badge>
          {i.missing.length > 0 && <small>Missing: {i.missing.join(', ')}</small>}
        </li>)}
      </ul>}
  </Drawer>;
}

function AttestDrawer({ slug, controlId, requirement, onClose, onDone }: { slug: string; controlId: string; requirement: Soc2Requirement; onClose: () => void; onDone: (m: string) => void }) {
  const queryClient = useQueryClient();
  const controls = useQuery({ queryKey: queryKeys.allControls(slug), queryFn: () => listAllControls(slug), enabled: !!slug, staleTime: 5 * 60_000 });
  const control = controls.data?.controls.find(c => c.control_id === controlId);
  const [summary, setSummary] = useState('');
  const [fileInfo, setFileInfo] = useState<{ name: string; sha: string } | null>(null);
  const [hashing, setHashing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [live, announce] = useAnnounce();

  const pickFile = async (f: File | undefined) => {
    setFileInfo(null);
    setErrors(e => { const n = { ...e }; delete n.file; return n; });
    if (!f) return;
    setHashing(true);
    try { const sha = await sha256Hex(await f.arrayBuffer()); setFileInfo({ name: f.name, sha }); announce(`${f.name} fingerprinted. The file stays on your device.`); }
    catch { setErrors(e => ({ ...e, file: 'This file could not be read.' })); }
    finally { setHashing(false); }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (saving || hashing) return; setFormError(null);
    if (summary.trim().length < 20) { setErrors({ summary: 'Describe what was done (at least 20 characters).' }); return; }
    setErrors({}); setSaving(true);
    try {
      await attestSoc2Control(slug, { control_id: controlId, summary: summary.trim(), evidence_sha256: fileInfo?.sha ?? null });
      await queryClient.invalidateQueries({ queryKey: ['soc2-readiness', slug] });
      onDone(`${requirement.ref} attested.`);
    } catch (err) {
      const b = err instanceof ApiError ? err.body as { code?: string; errors?: PostureFieldError[] } | undefined : undefined;
      if (b?.code === 'not_attestable') setFormError('This control is evidenced by a connected source, not by attestation. Connect the source in Setup.');
      else {
        const fe: Record<string, string> = {}; const rest: string[] = [];
        for (const x of b?.errors ?? []) { if (x.path === 'summary') fe.summary = x.message; else rest.push(x.message); }
        setErrors(fe);
        if (rest.length || !Object.keys(fe).length) setFormError(rest.join(' ') || (err instanceof Error ? err.message : 'The attestation could not be saved.'));
      }
    } finally { setSaving(false); }
  };

  return <Drawer open modal onClose={onClose} label={`Attest ${requirement.ref}`} className="v96-drawer" returnFocus={pageHeading}>
    <DrawerHead title={`Attest ${requirement.ref}`} onClose={onClose} />
    <div role="status" aria-live="polite" className="sr-only">{live}</div>
    <form className="v96-form" onSubmit={submit} noValidate>
      <div className="v96-attest-context">
        <span className="v96-meter-label">{requirement.ref} {requirement.title}</span>
        <span className="v75-muted">{control ? control.title : controls.isLoading ? 'Loading control…' : `Control ${controlId}`}</span>
      </div>
      {formError && <Alert variant="error">{formError}</Alert>}
      <Field id="attest-summary" label="What was done" required error={errors.summary} hint="Who did it, when, and where the record lives.">
        <Textarea rows={6} value={summary} onChange={e => setSummary(e.currentTarget.value)} />
      </Field>
      <Field id="attest-file" label="Supporting file (optional)" error={errors.file}
        hint={hashing ? 'Fingerprinting…' : fileInfo ? `${fileInfo.name} fingerprinted · stays on your device` : 'Only a fingerprint is recorded; the file is not uploaded.'}>
        <Input type="file" onChange={e => void pickFile(e.currentTarget.files?.[0])} />
      </Field>
      <div className="v75-actions">
        <Button type="submit" variant="primary" aria-disabled={saving || hashing || undefined}>{hashing ? 'Fingerprinting file…' : saving ? 'Saving…' : 'Attest'}</Button>
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </form>
  </Drawer>;
}

export function AcknowledgeDrawer({ slug, pending, onClose, onDone }: { slug: string; pending: PublishedPolicy[]; onClose: () => void; onDone: (n: number) => void }) {
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState(pending);
  const [updated, setUpdated] = useState(false);
  const [index, setIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [live, announce] = useAnnounce();
  const heading = useRef<HTMLHeadingElement>(null);
  const first = useRef(true);
  useEffect(() => { if (first.current) { first.current = false; return; } heading.current?.focus(); }, [index]);
  const policy = queue[index];

  const ack = async () => {
    if (!policy || saving) return;
    setSaving(true); setError(null);
    try {
      // Hash the exact text shown; the server rejects an acknowledgment of stale text.
      await acknowledgeSoc2Policy(slug, policy.version_id, await sha256Hex(policy.body_markdown));
      void queryClient.invalidateQueries({ queryKey: queryKeys.policyAckStatus(slug) });
      if (index + 1 >= queue.length) {
        // Refetch before closing: the "Acknowledge policies" opener disappears, so focus falls back to the page title.
        await queryClient.refetchQueries({ queryKey: pendingPolicyKeys.mine(slug) });
        onDone(queue.length);
      } else { void queryClient.invalidateQueries({ queryKey: pendingPolicyKeys.mine(slug) }); setUpdated(false); announce(`${policy.title} acknowledged.`); setIndex(index + 1); }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // The policy changed while it was open: load the current version in place and show its text.
        try {
          const fresh = await queryClient.fetchQuery({ queryKey: pendingPolicyKeys.published(slug), queryFn: () => listPublishedSoc2Policies(slug), staleTime: 0 });
          void queryClient.invalidateQueries({ queryKey: pendingPolicyKeys.mine(slug) });
          const current = fresh.policies.find(p => p.policy_key === policy.policy_key);
          if (current) { setQueue(q => q.map((p, i) => i === index ? current : p)); setUpdated(true); announce(`${current.title} was updated. Review version ${current.version}.`); }
          else { setError('This policy is no longer published.'); }
        } catch { setError('This policy changed. The new version could not load — try again.'); }
      } else setError(err instanceof Error ? err.message : 'The acknowledgment could not be saved.');
    } finally { setSaving(false); }
  };

  return <Drawer open modal onClose={onClose} label="Acknowledge policies" className="v96-drawer v96-drawer-wide" returnFocus={pageHeading}>
    <DrawerHead title={policy ? policy.title : 'Acknowledge policies'} onClose={onClose} headingRef={heading} />
    <div role="status" aria-live="polite" className="sr-only">{live}</div>
    {!policy ? <p>Nothing to acknowledge.</p> : <div className="v96-form">
      {queue.length > 1 && <p className="v75-muted">{`Policy ${index + 1} of ${queue.length} · version ${policy.version}`}</p>}
      {updated && <Alert variant="info" title="Updated policy">{`This is version ${policy.version}. Read it before acknowledging.`}</Alert>}
      {/* Safe subset of markdown, built as React elements — never raw HTML. The original bytes are what gets hashed. */}
      <div className="v96-policy-text" role="region" tabIndex={0} aria-label={`${policy.title} text`}><SafeMarkdown source={policy.body_markdown} /></div>
      {error && <Alert variant="error">{error}</Alert>}
      <div className="v75-actions">
        <Button variant="primary" aria-disabled={saving || undefined} onClick={() => void ack()}>{saving ? 'Saving…' : 'I have read and acknowledge'}</Button>
        <Button variant="ghost" onClick={onClose}>Later</Button>
      </div>
    </div>}
  </Drawer>;
}

export function PublishPolicyDrawer({ slug, published, onClose, onPublished, preset }: {
  slug: string; published: string[]; onClose: () => void; onPublished: (title: string, requiresAck: boolean) => void;
  /** A policy that isn't a SOC 2 template (e.g. the CMMC SSP): offered first and picked on open. */
  preset?: { key: string; title: string; note: string; body: string } | null;
}) {
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: ['soc2-policy-templates', slug], queryFn: () => listSoc2PolicyTemplates(slug), enabled: !!slug, staleTime: Infinity });
  const base = templates.data?.templates ?? [];
  const list = useMemo(() => (preset && !base.some((t) => t.key === preset.key)
    ? [{ key: preset.key, title: preset.title, body_markdown: preset.body, related_criteria: [], related_controls: [] }, ...base]
    : base), [base, preset]);
  const [key, setKey] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ack, setAck] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const pick = useCallback((k: string) => {
    const t = list.find(x => x.key === k);
    setKey(k); setErrors({});
    if (t) { setTitle(t.title); setBody(t.body_markdown); }
    // The SSP isn't a policy every member acknowledges (flow R3): off by default for the preset.
    if (preset) setAck(k !== preset.key);
  }, [list, preset]);
  useEffect(() => {
    if (key || list.length === 0) return;
    pick(((preset ? list.find(t => t.key === preset.key) : undefined) ?? list.find(t => !published.includes(t.key)) ?? list[0]!).key);
  }, [list, key, published, pick]);

  const submit = async (e: FormEvent) => {
    e.preventDefault(); if (saving) return; setFormError(null);
    const errs: Record<string, string> = {};
    if (title.trim().length < 3) errs.title = 'Title is required.';
    if (body.trim().length < 20) errs.body = 'Write the policy text (at least 20 characters).';
    setErrors(errs); if (Object.keys(errs).length) return;
    setSaving(true);
    try {
      await publishSoc2Policy(slug, { policy_key: key, title: title.trim(), body_markdown: body, requires_acknowledgment: ack });
      // Refetch before closing: the opener may be replaced, so the drawer's focus fallback (page title) applies.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: queryKeys.policyAckStatus(slug) }),
        queryClient.refetchQueries({ queryKey: pendingPolicyKeys.published(slug) }),
      ]);
      onPublished(title.trim(), ack);
    } catch (err) {
      const b = err instanceof ApiError ? err.body as { errors?: PostureFieldError[] } | undefined : undefined;
      const fe: Record<string, string> = {}; const rest: string[] = [];
      for (const x of b?.errors ?? []) { if (x.path === 'title') fe.title = x.message; else if (x.path === 'body_markdown') fe.body = x.message; else rest.push(x.message); }
      setErrors(fe);
      if (rest.length || !Object.keys(fe).length) setFormError(rest.join(' ') || (err instanceof Error ? err.message : 'The policy could not be published. Please retry.'));
    } finally { setSaving(false); }
  };

  return <Drawer open modal onClose={onClose} label="Publish a policy" className="v96-drawer" returnFocus={pageHeading}>
    <DrawerHead title="Publish a policy" onClose={onClose} />
    {templates.isLoading ? <Spinner label="Loading policy templates" />
      : templates.error ? <Alert variant="error"><p>Policy templates could not load.</p><Button onClick={() => void templates.refetch()}>Retry</Button></Alert>
      : <form className="v96-form" onSubmit={submit} noValidate>
        {formError && <Alert variant="error">{formError}</Alert>}
        {preset && key === preset.key ? <p className="v75-muted" data-part="preset-note">{preset.note}</p> : null}
        <Field id="policy-template" label="Policy">
          <Select value={key} onChange={e => pick(e.currentTarget.value)}>
            {list.map(t => <option key={t.key} value={t.key}>{t.title}{published.includes(t.key) ? ' (published — new version)' : ''}</option>)}
          </Select>
        </Field>
        <Field id="policy-title" label="Title" required error={errors.title}>
          <Input value={title} onChange={e => setTitle(e.currentTarget.value)} />
        </Field>
        <Field id="policy-body" label="Policy text" required error={errors.body} hint="Edit the starter text to match how you operate.">
          <Textarea rows={12} value={body} onChange={e => setBody(e.currentTarget.value)} />
        </Field>
        <Checkbox id="policy-ack" label="Members must acknowledge" checked={ack} onChange={e => setAck(e.currentTarget.checked)} />
        <div className="v75-actions">
          <Button type="submit" variant="primary" aria-disabled={saving || !key || undefined}>{saving ? 'Publishing…' : 'Publish policy'}</Button>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>}
  </Drawer>;
}
